import React from 'react';
import { Autocomplete, Box, SxProps, Theme } from '@mui/material';
import { InputField } from '@/pages/components/common/InputField';
import type { InvoiceEntryRow } from '@/pages/components/account/gl-invoice/create-stepper/context';
import { DR_CR_LINE_HEIGHT } from '@/pages/components/account/gl-invoice/ColumnSearchSelect';

export type InvoiceHistoryField =
    | 'description'
    | 'voucherNo'
    | 'invoiceNo'
    | 'chequeNo'
    | 'details';

export type InvoiceFieldHistory = Record<InvoiceHistoryField, string[]>;

const HISTORY_FIELDS: InvoiceHistoryField[] = [
    'description',
    'voucherNo',
    'invoiceNo',
    'chequeNo',
    'details',
];

/** 从已有行收集字段历史（新近优先、去重），供输入时搜索选择。 */
export function collectInvoiceFieldHistory(rows: InvoiceEntryRow[]): InvoiceFieldHistory {
    const buckets: Record<InvoiceHistoryField, string[]> = {
        description: [],
        voucherNo: [],
        invoiceNo: [],
        chequeNo: [],
        details: [],
    };
    const seen: Record<InvoiceHistoryField, Set<string>> = {
        description: new Set(),
        voucherNo: new Set(),
        invoiceNo: new Set(),
        chequeNo: new Set(),
        details: new Set(),
    };

    for (let i = rows.length - 1; i >= 0; i -= 1) {
        const row = rows[i];
        for (const field of HISTORY_FIELDS) {
            const raw = String(row[field] ?? '').trim();
            if (!raw || seen[field].has(raw)) continue;
            seen[field].add(raw);
            buckets[field].push(raw);
        }
    }
    return buckets;
}

interface HistorySuggestFieldProps {
    value: string;
    options: string[];
    onChange: (value: string) => void;
    placeholder?: string;
    width?: number | string;
    fullWidth?: boolean;
    autoFocus?: boolean;
    sx?: SxProps<Theme>;
}

const alignInputSx = {
    width: '100%',
    '& .MuiFormControl-root': { width: '100%' },
    '& .MuiTextField-root': { width: '100%' },
    '& .MuiInputBase-root': {
        width: '100%',
        height: 'auto',
        minHeight: DR_CR_LINE_HEIGHT - 4,
    },
    '& .MuiOutlinedInput-root.MuiInputBase-sizeSmall': {
        paddingTop: 0,
        paddingBottom: 0,
    },
    '& .MuiAutocomplete-inputRoot': {
        paddingTop: '0 !important',
        paddingBottom: '0 !important',
        paddingLeft: '0 !important',
        width: '100%',
    },
    '& .MuiAutocomplete-input': {
        minWidth: '0 !important',
        width: '100% !important',
        paddingTop: '4px !important',
        paddingBottom: '4px !important',
        paddingLeft: '14px !important',
        paddingRight: '14px !important',
    },
    '& .MuiAutocomplete-endAdornment': {
        position: 'absolute',
        right: 4,
        top: '50%',
        transform: 'translateY(-50%)',
        height: 'auto',
        maxHeight: '100%',
    },
} as const;

/** 自由输入 + 历史值下拉搜索（聚焦即展开）。 */
export function HistorySuggestField({
    value,
    options,
    onChange,
    placeholder,
    width,
    fullWidth,
    autoFocus,
    sx,
}: HistorySuggestFieldProps) {
    const fieldSx = {
        minWidth: 0,
        ...alignInputSx,
        ...((sx as object) || {}),
    } as const;

    const boxSx = fullWidth
        ? { width: '100%', minWidth: 0, flex: 1 }
        : { width: width ?? 100, minWidth: width ?? 100, maxWidth: width ?? 100, flexShrink: 0 };

    return (
        <Box sx={boxSx}>
            <Autocomplete
                freeSolo
                size="small"
                options={options}
                value={value || ''}
                inputValue={value || ''}
                onChange={(_, next) => onChange(typeof next === 'string' ? next : (next ?? ''))}
                onInputChange={(_, next, reason) => {
                    if (reason === 'input' || reason === 'clear') onChange(next);
                }}
                filterOptions={(opts, state) => {
                    const q = state.inputValue.trim().toLowerCase();
                    if (!q) return opts;
                    return opts.filter((o) => o.toLowerCase().includes(q));
                }}
                openOnFocus
                autoHighlight
                selectOnFocus
                handleHomeEndKeys
                fullWidth
                slotProps={{
                    popper: {
                        sx: {
                            width: 'auto !important',
                            minWidth: typeof width === 'number' ? Math.max(width, 180) : 180,
                            maxWidth: 420,
                        },
                    },
                    listbox: {
                        sx: {
                            '& .MuiAutocomplete-option': {
                                whiteSpace: 'nowrap',
                                fontSize: 13,
                            },
                        },
                    },
                }}
                sx={fieldSx}
                renderInput={(params) => (
                    <InputField
                        {...params}
                        margin="none"
                        fullWidth
                        autoFocus={autoFocus}
                        placeholder={placeholder}
                        sx={{ ...fieldSx, minWidth: 0, width: '100%' }}
                        InputProps={{ ...params.InputProps }}
                    />
                )}
            />
        </Box>
    );
}
